import React, { useEffect, useRef, useState } from "react";
import { api } from "../api";
import NextScenesConfirmDialog from "./NextScenesConfirmDialog";
import {
  novelJourneyPilotEligibleV2C1,
  novelJourneyPilotReadV2C1,
  novelJourneyPilotRecommendationV2C1,
  novelJourneyPilotConfirmationV2C1,
} from "../utils/novelJourneyPilotAdapterV2C1";

// V2C2: isolated owner UI candidate, inert until BOTH frontend pilot variables
// are explicitly configured and match the exact saved longform Story ID.
// This is NOT a release, publication approval, Story status update or PDF lock.
const clean = value => String(value ?? "").trim();
const CHECKPOINT_PATH = id => `/api/stories/${encodeURIComponent(id)}/journey-checkpoints-v2b2`;
const initial = (source, phase = "idle", payload = null, error = "") =>
  ({ source, phase, payload, error });

export default function NovelJourneyPilotWorkspaceV2C2({
  story, storyId, isStructurePreview = false, isOwner = false,
  journeyReadiness = {}, unassignedCanonCount = 0,
  chapterCount = 0, sceneCount = 0, publicationStatus = "inDevelopment",
  previewEnabled = false, previewBusy = false, finalApprovalEnabled = false,
  isFR = false, children,
}) {
  const canonicalId = clean(story?._id);
  const eligible = novelJourneyPilotEligibleV2C1({
    enabled: process.env.REACT_APP_NOVEL_JOURNEY_CHECKPOINT_V2C2_ENABLED === "true",
    configuredPilotId: process.env.REACT_APP_NOVEL_JOURNEY_CHECKPOINT_V2C2_PILOT_STORY_ID,
    canonicalStoryId: canonicalId, storyType: story?.storyType,
    isOwner, isStructurePreview,
  }) && canonicalId.toLowerCase() === clean(storyId).toLowerCase();
  const [progress, setProgress] = useState(() => initial(null));
  const [requestedStage, setRequestedStage] = useState("");
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState("");
  const generation = useRef(0);
  const submission = useRef(false);
  const visible = eligible && progress.source === story && progress.phase === "ready";
  const saved = visible ? novelJourneyPilotReadV2C1(progress.payload) : { ok: false };
  const readiness = { ...journeyReadiness, unassignedCanonCount };
  const recommendation = eligible
    ? novelJourneyPilotRecommendationV2C1({
        eligible: true, response: saved.ok ? progress.payload : null,
        publicationStatus, chapterCount, sceneCount, readiness,
        // Server does not yet attest a format fingerprint. The navigation
        // conservatively stays at Book Setup rather than claiming that
        // preparation, layout or the interior PDF has been approved.
        formatFingerprint: "", previewEnabled, previewBusy, finalApprovalEnabled,
      })
    : null;

  useEffect(() => {
    const epoch = ++generation.current;
    setRequestedStage("");
    setFeedback("");
    setBusy(false);
    submission.current = false;
    if (!eligible) {
      setProgress(initial(story));
      return undefined;
    }
    setProgress(initial(story, "loading"));
    api.get(CHECKPOINT_PATH(canonicalId)).then(payload => {
      if (generation.current !== epoch) return;
      const parsed = novelJourneyPilotReadV2C1(payload);
      setProgress(parsed.ok ? initial(story, "ready", payload) :
        initial(story, "unavailable", null, "Verified progress is unavailable."));
    }).catch(() => {
      if (generation.current === epoch) {
        setProgress(initial(story, "unavailable", null, "Verified progress is unavailable."));
      }
    });
    // Capture the ref object inside this effect for its own cleanup.
    // Only invalidate this exact generation; a newer Story keeps its token.
    const generationForCleanup = generation;
    return () => {
      if (generationForCleanup.current === epoch) generationForCleanup.current = epoch + 1;
    };
  }, [eligible, canonicalId, story]);

  async function confirmStage(stage) {
    if (!eligible || !saved.ok || submission.current || busy) return;
    if (stage === "manuscript" && journeyReadiness.manuscriptReady !== true) return;
    if (stage === "review" && saved.manuscript !== true) return;
    submission.current = true;
    setRequestedStage("");
    setBusy(true);
    setFeedback("");
    const epoch = generation.current;
    try {
      // Always re-read authenticated saved authority immediately before POST.
      const fresh = await api.get(CHECKPOINT_PATH(canonicalId));
      if (generation.current !== epoch) return;
      const latest = novelJourneyPilotReadV2C1(fresh);
      if (!latest.ok || latest.sourceFingerprint !== saved.sourceFingerprint ||
          latest.revision !== saved.revision) {
        throw new Error(isFR ? "Le manuscrit a changé. Actualisez et revérifiez." :
          "Saved progress changed. Refresh and review before confirming.");
      }
      const decision = novelJourneyPilotConfirmationV2C1({
        eligible: true, response: fresh, stage,
      });
      if (!decision.ok) throw new Error("This confirmation is not available.");
      const written = await api.post(CHECKPOINT_PATH(canonicalId), decision.request);
      if (generation.current !== epoch) return;
      if (written?.ok !== true || written?.stage !== stage) {
        throw new Error("Confirmation was not verified by the server.");
      }
      // POST does not return the full GET contract. Re-read saved progress,
      // and refuse to claim success without the corresponding effective stamp.
      const updated = await api.get(CHECKPOINT_PATH(canonicalId));
      if (generation.current !== epoch) return;
      const checked = novelJourneyPilotReadV2C1(updated);
      if (!checked.ok || checked[stage] !== true) {
        throw new Error("Confirmation could not be verified after saving.");
      }
      setProgress(initial(story, "ready", updated));
      setFeedback(isFR ? "Confirmation vérifiée et enregistrée." : "Confirmation saved and verified.");
    } catch (failure) {
      if (generation.current === epoch) {
        setProgress(initial(story, "unavailable", null, clean(failure?.message) ||
          "Could not verify saved progress. Reload before trying again."));
      }
    } finally {
      submission.current = false;
      if (generation.current === epoch) setBusy(false);
    }
  }

  const validForConfirm = eligible && saved.ok && !busy;
  const canManuscript = validForConfirm && journeyReadiness.manuscriptReady === true &&
    unassignedCanonCount === 0 && !["publishedamazon", "publishedexternal"].includes(clean(publicationStatus).toLowerCase());
  const canReview = validForConfirm && saved.manuscript === true &&
    !["publishedamazon", "publishedexternal"].includes(clean(publicationStatus).toLowerCase());
  const label = (en, fr) => isFR ? fr : en;

  return (
    <>
      {typeof children === "function" ? children({
        eligible, recommendation, loading: eligible && !visible,
      }) : null}
      {eligible ? (
        <section className="ns-card" data-novel-journey-checkpoint-v2c2="true"
          aria-label={label("Author manuscript checkpoints", "Confirmations de l'auteur")}
          style={{ padding: 17, borderRadius: 14, border: "1px solid #dae5df", display: "grid", gap: 10 }}>
          <div>
            <strong>{label("Author confirmations", "Confirmations de l'auteur")}</strong>
            <p className="ns-card-meta" style={{ margin: "5px 0 0" }}>
              {label("These are deliberate author decisions, not automatic completion or publication.",
                "Ces décisions sont prises par l'auteur et ne publient pas le livre.")}
            </p>
          </div>
          {!saved.ok ? (
            <p role="status" style={{ margin: 0, color: "#92400e" }}>
              {progress.phase === "loading" || progress.source !== story
                ? label("Checking saved progress…", "Vérification des étapes…")
                : clean(progress.error) || label("Verified progress is unavailable. No confirmation is permitted.",
                  "Progression indisponible. Confirmation impossible.")}
            </p>
          ) : (
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 240px", padding: 12, border: "1px solid #e2e8f0", borderRadius: 10 }}>
                <strong>{label("1. Manuscript Ready for Review", "1. Manuscrit prêt pour révision")}</strong>
                <p className="ns-card-meta" style={{ margin: "7px 0" }}>
                  {saved.manuscript ? label("Author-confirmed for this saved manuscript.", "Manuscrit enregistré confirmé.") :
                    label("Confirm only after completing and checking the saved manuscript.",
                      "Confirmer après vérification du manuscrit enregistré.")}
                </p>
                <button type="button" className="ns-button ns-button-outline"
                  data-novel-confirm-manuscript-v2c2="true" disabled={!canManuscript}
                  onClick={() => setRequestedStage("manuscript")}>
                  {saved.manuscript ? label("Reconfirm Manuscript", "Reconfirmer le manuscrit") :
                    label("Confirm Manuscript Ready", "Confirmer le manuscrit")}
                </button>
              </div>
              <div style={{ flex: "1 1 240px", padding: 12, border: "1px solid #e2e8f0", borderRadius: 10 }}>
                <strong>{label("2. Editorial Review Complete", "2. Révision éditoriale terminée")}</strong>
                <p className="ns-card-meta" style={{ margin: "7px 0" }}>
                  {saved.review ? label("Author-confirmed for this saved manuscript.", "Révision enregistrée confirmée.") :
                    label("Complete your editorial review before confirming.",
                      "Terminez la révision avant confirmation.")}
                </p>
                <button type="button" className="ns-button ns-button-outline"
                  data-novel-confirm-review-v2c2="true" disabled={!canReview || saved.review}
                  onClick={() => setRequestedStage("review")}>
                  {label("Confirm Review Complete", "Confirmer la révision")}
                </button>
              </div>
            </div>
          )}
          {feedback ? <p role="status" style={{ margin: 0 }}>{feedback}</p> : null}
          <NextScenesConfirmDialog open={Boolean(requestedStage) && saved.ok}
            title={requestedStage === "review"
              ? label("Confirm editorial review complete?", "Confirmer la fin de la révision ?")
              : label("Confirm manuscript ready for review?", "Confirmer le manuscrit prêt pour révision ?")}
            message={requestedStage === "review"
              ? label("You confirm you have completed editorial review of the current saved manuscript. This does not approve a PDF or publish the book.",
                "Vous confirmez la révision du manuscrit enregistré. Cela ne publie pas le livre.")
              : label("You confirm the current saved manuscript is ready for review. Reconfirming later clears any previous Review Complete decision.",
                "Vous confirmez que le manuscrit enregistré est prêt pour révision. Une reconfirmation annule la validation précédente de révision.")}
            stayLabel={label("Cancel", "Annuler")}
            leaveLabel={label("Yes, confirm", "Oui, confirmer")}
            onStay={() => setRequestedStage("")}
            onLeave={() => confirmStage(requestedStage)} />
        </section>
      ) : null}
    </>
  );
}
